import type { DataEditorRef, Rectangle } from "@glideapps/glide-data-grid";
import {
  type PointerEvent,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { DEFAULT_ROW_HEIGHT } from "./adapter";
import {
  clampRowHeight,
  findRowResizeBoundary,
  type RowResizeTarget,
} from "./row-resize";
import { getSavedSpreadsheetRow } from "./spreadsheet-region-view";

interface RowHeightPreview {
  scope: string;
  row: number;
  pixels: number;
}

interface RowDrag {
  scope: string;
  target: RowResizeTarget;
  pointerId: number;
  startY: number;
  startHeight: number;
  commit: (target: RowResizeTarget) => void;
}

export const useRowResize = ({
  enabled,
  scope,
  sheetId,
  rowMap,
  rowCount,
  frozenRows = 0,
  gridRef,
  getRowHeight,
  onResizeEnd,
}: {
  enabled: boolean;
  scope: string;
  sheetId: string | null;
  rowMap: number[];
  rowCount: number;
  frozenRows?: number;
  gridRef: RefObject<DataEditorRef | null>;
  getRowHeight: ((row: number) => number) | undefined;
  onResizeEnd: (target: RowResizeTarget) => void;
}) => {
  const [overrides, setOverrides] = useState<
    Record<string, Record<number, number>>
  >({});
  const [dragPreview, setDragPreview] = useState<RowHeightPreview | null>(null);
  const dragRef = useRef<RowDrag | null>(null);
  const visibleRowsRef = useRef<{
    scope: string;
    range: Rectangle;
    tx: number;
    ty: number;
  } | null>(null);
  const [isBoundaryHovered, setIsBoundaryHovered] = useState(false);

  const onVisibleRegionChanged = useCallback(
    (range: Rectangle, tx = 0, ty = 0) => {
      const previous = visibleRowsRef.current;
      visibleRowsRef.current = { scope, range, tx, ty };
      // Glide also reports selection and visible row-count changes. Those
      // leave the pointer's boundary in place; only scrolling clears hover.
      if (
        !dragRef.current &&
        previous?.scope === scope &&
        (previous.range.x !== range.x ||
          previous.range.y !== range.y ||
          previous.tx !== tx ||
          previous.ty !== ty)
      ) {
        setIsBoundaryHovered(false);
      }
    },
    [scope],
  );

  // Cancel an unfinished gesture if its sheet disappears. Completed overrides
  // stay scoped to their source workbook, sheet and saved row.
  useEffect(() => {
    dragRef.current = null;
    setDragPreview(null);
    setIsBoundaryHovered(false);
  }, [scope, enabled]);

  const rowHeight = useCallback(
    (displayRow: number) => {
      const row = getSavedSpreadsheetRow(rowMap, displayRow) + 1;
      if (dragPreview?.scope === scope && dragPreview.row === row) {
        return dragPreview.pixels;
      }
      return Math.max(
        DEFAULT_ROW_HEIGHT,
        overrides[scope]?.[row] ??
          getRowHeight?.(displayRow) ??
          DEFAULT_ROW_HEIGHT,
      );
    },
    [dragPreview, getRowHeight, overrides, rowMap, scope],
  );

  const findBoundary = (event: PointerEvent<HTMLDivElement>) => {
    if (!enabled) return null;
    const visibleRows = visibleRowsRef.current;
    if (visibleRows?.scope !== scope) return null;
    return findRowResizeBoundary({
      x: event.clientX,
      y: event.clientY,
      rowCount,
      frozenRows,
      visibleRows: visibleRows.range,
      getBounds: (row) => gridRef.current?.getBounds(-1, row),
    });
  };

  const onPointerDownCapture = (event: PointerEvent<HTMLDivElement>) => {
    if (!sheetId || event.button !== 0 || event.pointerType === "touch") return;
    const displayRow = findBoundary(event);
    if (displayRow === null) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    const pixels = rowHeight(displayRow);
    const drag: RowDrag = {
      scope,
      target: {
        sheetId,
        row: getSavedSpreadsheetRow(rowMap, displayRow) + 1,
        pixels,
      },
      pointerId: event.pointerId,
      startY: event.clientY,
      startHeight: pixels,
      commit: onResizeEnd,
    };
    dragRef.current = drag;
    setDragPreview({ scope, row: drag.target.row, pixels });
  };

  const onPointerMoveCapture = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) {
      setIsBoundaryHovered(
        event.pointerType !== "touch" && findBoundary(event) !== null,
      );
      return;
    }
    if (event.pointerId !== drag.pointerId) return;
    event.preventDefault();
    event.stopPropagation();
    const next = {
      ...drag,
      target: {
        ...drag.target,
        pixels: clampRowHeight(
          drag.startHeight + event.clientY - drag.startY,
          DEFAULT_ROW_HEIGHT,
        ),
      },
    };
    dragRef.current = next;
    setDragPreview({
      scope: drag.scope,
      row: next.target.row,
      pixels: next.target.pixels,
    });
  };

  const finishDrag = (event: PointerEvent<HTMLDivElement>, save: boolean) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = null;
    setDragPreview(null);
    setIsBoundaryHovered(
      save && event.pointerType !== "touch" && findBoundary(event) !== null,
    );
    event.currentTarget.releasePointerCapture(event.pointerId);
    if (!save || drag.target.pixels === drag.startHeight) return;
    setOverrides((current) => ({
      ...current,
      [drag.scope]: {
        ...current[drag.scope],
        [drag.target.row]: drag.target.pixels,
      },
    }));
    drag.commit(drag.target);
  };

  return {
    rowHeight,
    onVisibleRegionChanged,
    isResizingRow: isBoundaryHovered || dragPreview?.scope === scope,
    onPointerDownCapture,
    onPointerMoveCapture,
    onPointerUpCapture: (event: PointerEvent<HTMLDivElement>) =>
      finishDrag(event, true),
    onPointerCancelCapture: (event: PointerEvent<HTMLDivElement>) =>
      finishDrag(event, false),
    onPointerLeave: () => setIsBoundaryHovered(false),
  };
};
